/**
 * Upload View Component
 * Handles PDF/Excel upload and extraction workflow
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Tabs,
  Tab,
  Alert,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PlayArrow as ExtractIcon,
  Delete as DeleteIcon,
  GetApp as ExportIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';
import { useBOM } from '../contexts/BOMContext';
import { useAuth } from '../contexts/AuthContext';
import * as bomApi from '../services/bomApi';
import FileUploadZone from './FileUploadZone';
import PDFViewer from './PDFViewer';
import ExcelViewer from './ExcelViewer';
import BOMTable from './BOMTable';
import ComponentsTab from './tabs/ComponentsTab';
import ManufacturersTab from './tabs/ManufacturersTab';
import MetadataTab from './tabs/MetadataTab';
import FeaturesTab from './tabs/FeaturesTab';
import DimensionsTab from './tabs/DimensionsTab';
import { AIModel, BOMItem, CustomerFolder } from '../types/bom.types';

// Helper function to extract original filename
const extractOriginalFilename = (filename: string): string => {
  if (!filename) return filename;
  if (filename.includes('_')) {
    const parts = filename.split('_', 2);
    // Check if first part is a 32-character hex string (UUID without dashes)
    if (parts[0].length === 32 && /^[0-9a-f]{32}$/i.test(parts[0])) {
      return parts[1]; // Return the part after the UUID
    }
  }
  return filename;
};

const UploadView: React.FC = () => {
  const { user } = useAuth();
  const {
    uploadedPdfFilename,
    uploadedPdfUrl,
    uploadedXlsFilename,
    uploadedXlsUrl,
    setUploadedPdf,
    setUploadedXls,
    clearUploadedPdf,
    clearUploadedXls,
    currentPage,
    totalPages,
    extractedItems,
    selectedModel,
    forceReextract,
    setCurrentPage,
    setTotalPages,
    addExtractedItems,
    clearExtractedItems,
    setSelectedModel,
    setForceReextract,
    setLoading,
    setLoadingProgress,
    showToast,
    components,
    manufacturers,
    metadata,
    dimensions,
    features,
    setComponents,
    setManufacturers,
    setMetadata,
    setDimensions,
    setFeatures,
  } = useBOM();

  const [currentTab, setCurrentTab] = useState<string>('bom');
  const [isExtracting, setIsExtracting] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);
  const [expandedViewer, setExpandedViewer] = useState<'pdf' | 'excel' | null>(null);
  const [selectedItem, setSelectedItem] = useState<BOMItem | null>(null);
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false);
  const [previousPdfs, setPreviousPdfs] = useState<Array<{filename: string; original_name: string; url: string; uploaded_at: string}>>([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  
  // Customer folder state
  const [customerFolders, setCustomerFolders] = useState<CustomerFolder[]>([]);
  const [selectedUploadFolder, setSelectedUploadFolder] = useState<string>('');
  
  // Extraction options dialog state
  const [extractOptionsOpen, setExtractOptionsOpen] = useState(false);
  const [pagesToSkip, setPagesToSkip] = useState<string>('');
  const [useCustomerRules, setUseCustomerRules] = useState(true);
  const [extractionFolderId, setExtractionFolderId] = useState<string>('');
  
  // Reset showFinalize when extraction is cleared (e.g., after deletion)
  React.useEffect(() => {
    if (uploadedPdfFilename && extractedItems.length === 0) {
      setShowFinalize(false);
    }
  }, [extractedItems.length, uploadedPdfFilename]);

  // Auto-load last extraction on first mount
  useEffect(() => {
    const loadLastExtraction = async () => {
      // Only load if nothing is currently loaded
      if (uploadedPdfFilename || extractedItems.length > 0) {
        return; // Already have something loaded
      }

      try {
        setLoading(true, 'Loading last extraction...');
        
        // Get extraction history
        const historyData = await bomApi.getExtractionHistory();
        
        if (historyData.history && historyData.history.length > 0) {
          // Get the most recent extraction (first in the list)
          const lastExtraction = historyData.history[0];
          
          // Load the extraction data
          const loadedData = await bomApi.loadExtraction(lastExtraction.job_id);
          
          // Set PDF and Excel files
          setUploadedPdf(lastExtraction.pdf_filename, lastExtraction.pdf_url);
          if (lastExtraction.xls_filename && lastExtraction.xls_url) {
            setUploadedXls(lastExtraction.xls_filename, lastExtraction.xls_url);
          }
          
          // Load all extraction data - clear first to avoid duplicates
          clearExtractedItems();
          addExtractedItems(loadedData.items);
          
          // Derive components and manufacturers from loaded items
          const manufacturerMap = new Map<string, {
            manufacturer: string;
            component_count: number;
            components: string[];
            pages: number[];
          }>();
          
          const componentMap = new Map<string, {
            type: string;
            count: number;
            examples: string[];
            pages: number[];
          }>();
          
          loadedData.items.forEach((item: any) => {
            // Process manufacturer
            const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
            if (!manufacturerMap.has(mfr)) {
              manufacturerMap.set(mfr, {
                manufacturer: mfr,
                component_count: 0,
                components: [],
                pages: [],
              });
            }
            const mfrData = manufacturerMap.get(mfr)!;
            const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
            mfrData.component_count += qty;
            const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
            if (!mfrData.components.includes(ref)) {
              mfrData.components.push(ref);
            }
            const page = item.page || (item.pages && item.pages[0]) || 1;
            if (page && !mfrData.pages.includes(page)) {
              mfrData.pages.push(page);
            }
            
            // Process component type
            const compType = item.component_type || item.type || 'Unknown';
            if (!componentMap.has(compType)) {
              componentMap.set(compType, {
                type: compType,
                count: 0,
                examples: [],
                pages: [],
              });
            }
            const compData = componentMap.get(compType)!;
            compData.count += qty;
            if (!compData.examples.includes(ref)) {
              compData.examples.push(ref);
            }
            if (page && !compData.pages.includes(page)) {
              compData.pages.push(page);
            }
          });
          
          const derivedManufacturers = Array.from(manufacturerMap.values())
            .sort((a, b) => b.component_count - a.component_count);
          const derivedComponents = Array.from(componentMap.values())
            .sort((a, b) => b.count - a.count);
          
          setComponents(derivedComponents);
          setManufacturers(derivedManufacturers);
          setMetadata(loadedData.metadata || null);
          setFeatures(loadedData.features || []);
          setDimensions(loadedData.dimensions || []);
          setTotalPages(loadedData.total_pages || 1);
          setCurrentPage(1);
          
          showToast('success', `Loaded last extraction: ${lastExtraction.pdf_filename}`);
        }
      } catch (error: any) {
        // Silently fail - it's okay if there's no previous extraction
        console.log('No previous extraction to load:', error.message);
      } finally {
        setLoading(false);
      }
    };

    loadLastExtraction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Load customer folders on mount
  useEffect(() => {
    const loadFolders = async () => {
      try {
        const folders = await bomApi.listFolders();
        setCustomerFolders(folders);
      } catch (error) {
        console.error('Failed to load customer folders:', error);
      }
    };
    loadFolders();
  }, []);

  // Load previous PDFs with extractions
  useEffect(() => {
    const loadPreviousPdfs = async () => {
      setLoadingPdfs(true);
      try {
        const historyData = await bomApi.getExtractionHistory();
        if (historyData.history && historyData.history.length > 0) {
          // Get unique PDFs (only those with extractions)
          const pdfMap = new Map<string, any>();
          historyData.history.forEach((item: any) => {
            if (item.pdf_filename && !pdfMap.has(item.pdf_filename)) {
              pdfMap.set(item.pdf_filename, {
                filename: item.pdf_filename,
                original_name: extractOriginalFilename(item.pdf_filename),
                url: item.pdf_url,
                uploaded_at: item.completed_at || item.created_at
              });
            }
          });
          setPreviousPdfs(Array.from(pdfMap.values()));
        }
      } catch (error: any) {
        console.log('Failed to load previous PDFs:', error.message);
      } finally {
        setLoadingPdfs(false);
      }
    };

    loadPreviousPdfs();
  }, []);

  // Handle selecting a previous PDF
  const handleSelectPreviousPdf = useCallback(async (pdfInfo: any) => {
    try {
      setLoading(true, 'Loading PDF and extraction...');
      
      // Set PDF
      setUploadedPdf(pdfInfo.filename, pdfInfo.url);
      
      // Load the latest extraction for this PDF
      const historyData = await bomApi.getExtractionHistory();
      const pdfExtractions = historyData.history.filter((item: any) => item.pdf_filename === pdfInfo.filename);
      
      if (pdfExtractions.length > 0) {
        const latestExtraction = pdfExtractions[0]; // Already sorted by date
        const loadedData = await bomApi.loadExtraction(latestExtraction.job_id);
        
        // Clear and load extraction data
        clearExtractedItems();
        addExtractedItems(loadedData.items);
        
        // Set Excel if available
        if (latestExtraction.xls_filename && latestExtraction.xls_url) {
          setUploadedXls(latestExtraction.xls_filename, latestExtraction.xls_url);
        }
        
        // Derive components and manufacturers
        const manufacturerMap = new Map<string, any>();
        const componentMap = new Map<string, any>();
        
        loadedData.items.forEach((item: any) => {
          const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
          if (!manufacturerMap.has(mfr)) {
            manufacturerMap.set(mfr, {
              manufacturer: mfr,
              component_count: 0,
              components: [],
              pages: [],
            });
          }
          const mfrData = manufacturerMap.get(mfr)!;
          const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
          mfrData.component_count += qty;
          const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
          if (!mfrData.components.includes(ref)) {
            mfrData.components.push(ref);
          }
          const page = item.page || (item.pages && item.pages[0]) || 1;
          if (page && !mfrData.pages.includes(page)) {
            mfrData.pages.push(page);
          }
          
          const compType = item.component_type || item.type || 'Unknown';
          if (!componentMap.has(compType)) {
            componentMap.set(compType, {
              type: compType,
              count: 0,
              examples: [],
              pages: [],
            });
          }
          const compData = componentMap.get(compType)!;
          compData.count += qty;
          if (!compData.examples.includes(ref)) {
            compData.examples.push(ref);
          }
          if (page && !compData.pages.includes(page)) {
            compData.pages.push(page);
          }
        });
        
        const derivedManufacturers = Array.from(manufacturerMap.values())
          .sort((a, b) => b.component_count - a.component_count);
        const derivedComponents = Array.from(componentMap.values())
          .sort((a, b) => b.count - a.count);
        
        setComponents(derivedComponents);
        setManufacturers(derivedManufacturers);
        setMetadata(loadedData.metadata || null);
        setFeatures(loadedData.features || []);
        setDimensions(loadedData.dimensions || []);
        setTotalPages(loadedData.total_pages || 1);
        setCurrentPage(1);
        
        showToast('success', `Loaded ${extractOriginalFilename(pdfInfo.filename)} with ${loadedData.items.length} items`);
        setFileDrawerOpen(false);
      }
    } catch (error: any) {
      showToast('error', `Failed to load PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setUploadedPdf, setUploadedXls, clearExtractedItems, addExtractedItems, setComponents, setManufacturers, setMetadata, setFeatures, setDimensions, setTotalPages, setCurrentPage, showToast]);
  
  // New states for UI integration
  const [showCompareMode, setShowCompareMode] = useState(false);
  const [comparePageNum, setComparePageNum] = useState<string>('1');
  const [compareLLMModel, setCompareLLMModel] = useState('claude');
  const [comparisonResult, setComparisonResult] = useState<any>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [extractedBomData, setExtractedBomData] = useState<{main_bom: any[], auxiliary_bom: any[]} | null>(null);

  // Helper function to convert backend item to BOMItem format with all fields
  const convertBackendItemToBOMItem = useCallback((item: any, isAuxiliary: boolean = false): BOMItem => {
    return {
      reference: item.reference,
      description: item.description,
      manufacturer: item.manufacturer_suggestion,
      manufacturer_suggestion: item.manufacturer_suggestion,
      part_number: item.part_number,
      pn_guess: item.pn_guess,
      quantity: item.qty,
      qty: item.qty,
      confidence: item.confidence,
      component_type: item.component_type,
      type: item.component_type,
      notes: isAuxiliary ? (item.notes || item.calculation_basis) : item.notes,
      page: item.page,
      pages: item.pages || (item.page ? [item.page] : []),
      rating_tokens: item.rating_tokens,
      raw_line: item.raw_line,
      // Pass ERP match data
      erp_match: item.erp_match,
      internal_part_number: item.erp_match?.internal_part_number,
      // Pass all other fields from backend
      ...item,
      _isAuxiliary: isAuxiliary
    };
  }, []);

  // Utility function to derive manufacturers and components from BOM items
  const deriveTabData = useCallback((bomItems: BOMItem[]) => {
    // Derive manufacturers
    const manufacturerMap = new Map<string, {
      manufacturer: string;
      component_count: number;
      components: string[];
      pages: number[];
    }>();

    // Derive components
    const componentMap = new Map<string, {
      type: string;
      count: number;
      examples: string[];
      pages: number[];
    }>();

    bomItems.forEach((item) => {
      // Process manufacturer
      const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
      if (!manufacturerMap.has(mfr)) {
        manufacturerMap.set(mfr, {
          manufacturer: mfr,
          component_count: 0,
          components: [],
          pages: [],
        });
      }
      const mfrData = manufacturerMap.get(mfr)!;
      const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
      mfrData.component_count += qty;
      const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
      if (!mfrData.components.includes(ref)) {
        mfrData.components.push(ref);
      }
      if (item.page && !mfrData.pages.includes(item.page)) {
        mfrData.pages.push(item.page);
      }

      // Process component type
      const compType = item.component_type || item.type || 'Unknown';
      if (!componentMap.has(compType)) {
        componentMap.set(compType, {
          type: compType,
          count: 0,
          examples: [],
          pages: [],
        });
      }
      const compData = componentMap.get(compType)!;
      compData.count += qty;
      if (!compData.examples.includes(ref)) {
        compData.examples.push(ref);
      }
      if (item.page && !compData.pages.includes(item.page)) {
        compData.pages.push(item.page);
      }
    });

    // Convert maps to arrays and sort
    const manufacturers = Array.from(manufacturerMap.values())
      .sort((a, b) => b.component_count - a.component_count);
    
    const components = Array.from(componentMap.values())
      .sort((a, b) => b.count - a.count);

    setManufacturers(manufacturers);
    setComponents(components);
  }, [setManufacturers, setComponents]);

  // Handle PDF upload
  const handlePdfUpload = useCallback(
    async (file: File) => {
      setLoading(true, `Uploading ${file.name}...`);
      try {
        const result = await bomApi.uploadPDF(file);
        setUploadedPdf(result.filename, result.url);
        
        // Assign to customer folder if selected
        if (selectedUploadFolder && result.filename) {
          try {
            setLoading(true, 'Assigning to customer folder...');
            // We need to get the job ID for the file - use findExtractionByPdf or create a temp job
            // For now, we'll set the folder context for the extraction
            setExtractionFolderId(selectedUploadFolder);
            const folderName = customerFolders.find(f => f.id === selectedUploadFolder)?.customer_name;
            showToast('info', `File will use rules from: ${folderName}`);
          } catch (folderError) {
            console.error('Failed to assign to folder:', folderError);
            // Continue anyway - file is uploaded
          }
        }
        
        if (result.replaced_existing) {
          showToast('info', `Replaced existing PDF '${result.original_name}' and deleted all extractions. Ready for new extraction.`);
          // Skip auto-load since we just deleted the extraction
        } else {
          showToast('success', `${result.original_name} uploaded successfully`);
          
          // Auto-load extraction if it exists for this PDF (only if not replaced)
          setLoading(true, 'Checking for existing extraction...');
          try {
            const extractionInfo = await bomApi.findExtractionByPdf(result.filename);
            
            if (extractionInfo.exists && extractionInfo.job_id) {
              // Load the extraction data
              setLoading(true, 'Loading existing extraction...');
              const loadedData = await bomApi.loadExtraction(extractionInfo.job_id);
              
              // Set PDF and Excel files (if Excel exists)
              setUploadedPdf(extractionInfo.pdf_filename || result.filename, extractionInfo.pdf_url || result.url);
              if (extractionInfo.xls_filename && extractionInfo.xls_url) {
                setUploadedXls(extractionInfo.xls_filename, extractionInfo.xls_url);
              }
              
              // Load all extraction data
              clearExtractedItems();
              addExtractedItems(loadedData.items);
              // Derive components and manufacturers from loaded items
              const manufacturerMap = new Map<string, {
                manufacturer: string;
                component_count: number;
                components: string[];
                pages: number[];
              }>();
              
              const componentMap = new Map<string, {
                type: string;
                count: number;
                examples: string[];
                pages: number[];
              }>();
              
              loadedData.items.forEach((item: any) => {
                // Process manufacturer
                const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
                if (!manufacturerMap.has(mfr)) {
                  manufacturerMap.set(mfr, {
                    manufacturer: mfr,
                    component_count: 0,
                    components: [],
                    pages: [],
                  });
                }
                const mfrData = manufacturerMap.get(mfr)!;
                const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
                mfrData.component_count += qty;
                const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
                if (!mfrData.components.includes(ref)) {
                  mfrData.components.push(ref);
                }
                const page = item.page || (item.pages && item.pages[0]) || 1;
                if (page && !mfrData.pages.includes(page)) {
                  mfrData.pages.push(page);
                }
                
                // Process component type
                const compType = item.component_type || item.type || 'Unknown';
                if (!componentMap.has(compType)) {
                  componentMap.set(compType, {
                    type: compType,
                    count: 0,
                    examples: [],
                    pages: [],
                  });
                }
                const compData = componentMap.get(compType)!;
                compData.count += qty;
                if (!compData.examples.includes(ref)) {
                  compData.examples.push(ref);
                }
                if (page && !compData.pages.includes(page)) {
                  compData.pages.push(page);
                }
              });
              
              const derivedManufacturers = Array.from(manufacturerMap.values())
                .sort((a, b) => b.component_count - a.component_count);
              const derivedComponents = Array.from(componentMap.values())
                .sort((a, b) => b.count - a.count);
              
              setComponents(derivedComponents);
              setManufacturers(derivedManufacturers);
              setMetadata(loadedData.metadata || null);
              setFeatures(loadedData.features || []);
              setDimensions(loadedData.dimensions || []);
              setTotalPages(loadedData.total_pages || 1);
              setCurrentPage(2);
              
              showToast('success', `Auto-loaded extraction with ${loadedData.items.length} items`);
              setCurrentTab('bom');
            }
          } catch (extractionError: any) {
            // Silently fail - extraction might not exist yet
            console.log('No existing extraction found:', extractionError.message);
          }
        }
      } catch (error: any) {
        showToast('error', `Upload failed: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [setUploadedPdf, setUploadedXls, setLoading, showToast, clearExtractedItems, addExtractedItems, setComponents, setManufacturers, setMetadata, setFeatures, setDimensions, setTotalPages, setCurrentPage, setShowFinalize, setCurrentTab, selectedUploadFolder, customerFolders]
  );

  // Handle Excel upload
  const handleExcelUpload = useCallback(
    async (file: File) => {
      setLoading(true, `Uploading ${file.name}...`);
      try {
        const result = await bomApi.uploadExcel(file);
        setUploadedXls(result.filename, result.url);
        
        if (result.already_exists) {
          showToast('info', `File already exists: ${result.original_name}`);
        } else {
          showToast('success', `${result.original_name} uploaded successfully`);
        }
      } catch (error: any) {
        showToast('error', `Upload failed: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [setUploadedXls, setLoading, showToast]
  );

  // Handle inference extraction (NEW - full PDF at once)
  const handleInferenceExtraction = useCallback(async () => {
    if (!uploadedPdfUrl || !uploadedPdfFilename) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsExtracting(true);
    setLoadingProgress({ message: 'Checking for existing extraction...', progress: 0, logs: [] });
    setLoading(true, 'Checking for existing extraction...');
    
    try {
      // Check if extraction already exists
      const existingCheck = await bomApi.checkExistingExtraction(uploadedPdfFilename, 'inference_engine');
      
      if (existingCheck.exists && existingCheck.job_id) {
        // Load existing extraction
        setLoading(true, 'Loading existing extraction...');
        const loadedData = await bomApi.loadExtraction(existingCheck.job_id);
        
        // Clear existing data
        clearExtractedItems();
        
        // Load all data from stored extraction
        addExtractedItems(loadedData.items);
        // Derive components and manufacturers from loaded items
        const manufacturerMap = new Map<string, {
          manufacturer: string;
          component_count: number;
          components: string[];
          pages: number[];
        }>();
        
        const componentMap = new Map<string, {
          type: string;
          count: number;
          examples: string[];
          pages: number[];
        }>();
        
        loadedData.items.forEach((item: any) => {
          // Process manufacturer
          const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
          if (!manufacturerMap.has(mfr)) {
            manufacturerMap.set(mfr, {
              manufacturer: mfr,
              component_count: 0,
              components: [],
              pages: [],
            });
          }
          const mfrData = manufacturerMap.get(mfr)!;
          const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
          mfrData.component_count += qty;
          const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
          if (!mfrData.components.includes(ref)) {
            mfrData.components.push(ref);
          }
          const page = item.page || (item.pages && item.pages[0]) || 1;
          if (page && !mfrData.pages.includes(page)) {
            mfrData.pages.push(page);
          }
          
          // Process component type
          const compType = item.component_type || item.type || 'Unknown';
          if (!componentMap.has(compType)) {
            componentMap.set(compType, {
              type: compType,
              count: 0,
              examples: [],
              pages: [],
            });
          }
          const compData = componentMap.get(compType)!;
          compData.count += qty;
          if (!compData.examples.includes(ref)) {
            compData.examples.push(ref);
          }
          if (page && !compData.pages.includes(page)) {
            compData.pages.push(page);
          }
        });
        
        const derivedManufacturers = Array.from(manufacturerMap.values())
          .sort((a, b) => b.component_count - a.component_count);
        const derivedComponents = Array.from(componentMap.values())
          .sort((a, b) => b.count - a.count);
        
        setComponents(derivedComponents);
        setManufacturers(derivedManufacturers);
        setMetadata(loadedData.metadata || null);
        setFeatures(loadedData.features || []);
        setDimensions(loadedData.dimensions || []);
        setTotalPages(loadedData.total_pages || 1);
        setCurrentPage(2);
        
        showToast('success', `Loaded existing extraction with ${loadedData.items.length} items`);
        setCurrentTab('bom');
        return;
      }
      
      // No existing extraction, run new extraction
      setLoadingProgress({ message: 'Starting extraction...', progress: 0, logs: [] });
      setLoading(true, 'Extracting BOM with inference engine...');
      
      // Use already-uploaded file - NO DOWNLOAD NEEDED
      if (!uploadedPdfFilename) {
        throw new Error('PDF filename is not available');
      }
      
      console.log(`📥 Using already-uploaded file for inference: ${uploadedPdfFilename}`);
      
      // Call inference API with streaming progress
      const result = await bomApi.extractAllPages(
        null,  // No file upload needed
        false,
        (progress) => {
          // Real-time progress updates - accumulate logs
          setLoadingProgress((prev) => ({
            message: progress.message,
            progress: progress.progress,
            logs: prev ? [...prev.logs, progress.message].slice(-20) : [progress.message]
          }));
          setLoading(true, progress.message);
        },
        (completeResult) => {
          // Extraction complete
          setLoadingProgress({
            message: 'Extraction complete!',
            progress: 100,
            logs: []
          });
        },
        (error) => {
          // Error occurred
          showToast('error', `Extraction failed: ${error}`);
          setLoading(false);
          setIsExtracting(false);
        },
        uploadedPdfFilename  // Pass filename to use already-uploaded file
      );
      
      // Validate result structure
      if (!result || !result.main_bom || !result.auxiliary_bom) {
        throw new Error('Invalid extraction result: missing main_bom or auxiliary_bom');
      }
      
      // Clear existing data
      clearExtractedItems();
      setComponents([]);
      setManufacturers([]);
      setFeatures([]);
      setDimensions([]);
      
      // Convert inference results to BOM items format - use helper to pass all fields
      const mainBomItems: BOMItem[] = (result.main_bom || []).map((item: any) => convertBackendItemToBOMItem(item, false));
      const auxiliaryBomItems: BOMItem[] = (result.auxiliary_bom || []).map((item: any) => convertBackendItemToBOMItem(item, true));
      
      // Add all items to the BOM
      const allItems = [...mainBomItems, ...auxiliaryBomItems];
      addExtractedItems(allItems);
      
      // Derive manufacturers and components data for tabs
      deriveTabData(allItems);
      
      // Derive tab data synchronously for storage
      const manufacturerMap = new Map<string, any>();
      const componentMap = new Map<string, any>();
      
      allItems.forEach((item) => {
        const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
        if (!manufacturerMap.has(mfr)) {
          manufacturerMap.set(mfr, {
            manufacturer: mfr,
            component_count: 0,
            components: [],
            pages: [],
          });
        }
        const mfrData = manufacturerMap.get(mfr)!;
        const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
        mfrData.component_count += qty;
        const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
        if (!mfrData.components.includes(ref)) {
          mfrData.components.push(ref);
        }
        if (item.page && !mfrData.pages.includes(item.page)) {
          mfrData.pages.push(item.page);
        }
        
        const compType = item.component_type || item.type || 'Unknown';
        if (!componentMap.has(compType)) {
          componentMap.set(compType, {
            type: compType,
            count: 0,
            examples: [],
            pages: [],
          });
        }
        const compData = componentMap.get(compType)!;
        compData.count += qty;
        if (!compData.examples.includes(ref)) {
          compData.examples.push(ref);
        }
        if (item.page && !compData.pages.includes(item.page)) {
          compData.pages.push(item.page);
        }
      });
      
      const derivedManufacturers = Array.from(manufacturerMap.values())
        .sort((a, b) => b.component_count - a.component_count);
      const derivedComponents = Array.from(componentMap.values())
        .sort((a, b) => b.count - a.count);
      
      // Set totals
      setTotalPages(1);
      setCurrentPage(2); // Mark as complete
      
      // Automatically save extraction to database
      setLoading(true, 'Saving extraction results...');
      try {
        // Create a job via finalize endpoint to store the extraction
        const jobResult = await bomApi.finalizeExtraction(
          uploadedPdfFilename,
          uploadedXlsFilename,
          allItems,
          'inference_engine'
        );
        
        // Attach extraction_ids to items for verification
        const result = jobResult as any;
        if (result.extraction_ids && result.extraction_ids.length === allItems.length) {
          allItems.forEach((item, idx) => {
            (item as any).extraction_id = result.extraction_ids[idx];
          });
          // Update state with IDs
          addExtractedItems(allItems);
        }
        
        // Store complete extraction data (all tabs) - use derived data
        await bomApi.storeExtractionData(jobResult.job_id, {
          items: allItems,
          components: derivedComponents,
          manufacturers: derivedManufacturers,
          metadata: metadata || null,
          features: features || [],
          dimensions: dimensions || []
        });
        
        showToast('success', 'Extraction saved successfully!');
      } catch (saveError: any) {
        showToast('error', `Extraction completed but failed to save: ${saveError.message}`);
      } finally {
        setLoading(false);
      }
      
      // Show success message with stats
      const stats = result.stats;
      showToast(
        'success',
        `Extracted ${stats.main_items} items (${stats.high_confidence_count} high confidence) + ${stats.auxiliary_items} auxiliary items. KB v${result.kb_version}`
      );
      
      // Switch to BOM tab
      setCurrentTab('bom');
    } catch (error: any) {
      showToast('error', `Inference extraction failed: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoading(false);
    }
  }, [
    uploadedPdfUrl,
    uploadedPdfFilename,
    uploadedXlsFilename,
    clearExtractedItems,
    addExtractedItems,
    setComponents,
    setManufacturers,
    setMetadata,
    setFeatures,
    setDimensions,
    setTotalPages,
    setCurrentPage,
    setLoading,
    showToast,
    deriveTabData,
    components,
    manufacturers,
    metadata,
    features,
    dimensions,
  ]);

  // Open extraction options dialog
  const handleExtractClick = useCallback(() => {
    if (!uploadedPdfUrl) {
      showToast('error', 'Please upload a PDF first');
      return;
    }
    // Set initial folder ID from upload selection
    setExtractionFolderId(selectedUploadFolder);
    setExtractOptionsOpen(true);
  }, [uploadedPdfUrl, showToast, selectedUploadFolder]);

  // Parse pages to skip into selected pages array
  const parseSelectedPages = useCallback((skipPagesStr: string, total: number): number[] | null => {
    if (!skipPagesStr.trim()) {
      return null; // Process all pages
    }
    
    // Parse pages to skip (e.g., "1,3,5-7" -> [1,3,5,6,7])
    const skipPages = new Set<number>();
    const parts = skipPagesStr.split(',').map(s => s.trim());
    
    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(n => parseInt(n.trim()));
        if (!isNaN(start) && !isNaN(end)) {
          for (let i = start; i <= end; i++) {
            skipPages.add(i);
          }
        }
      } else {
        const page = parseInt(part);
        if (!isNaN(page)) {
          skipPages.add(page);
        }
      }
    }
    
    // Generate selected pages (all except skipped)
    const selectedPages: number[] = [];
    for (let i = 1; i <= total; i++) {
      if (!skipPages.has(i)) {
        selectedPages.push(i);
      }
    }
    
    return selectedPages.length > 0 ? selectedPages : null;
  }, []);

  // NEW: Extract All Pages (called after options dialog)
  const handleExtractAll = useCallback(async () => {
    setExtractOptionsOpen(false);
    
    if (!uploadedPdfUrl) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsExtracting(true);
    setLoading(true, 'Preparing extraction...');
    
    try {
      // Use already-uploaded file instead of re-downloading
      if (!uploadedPdfFilename) {
        throw new Error('PDF filename is not available');
      }
      
      // Parse pages to skip (default to 100 pages max for now, will be determined from PDF)
      const selectedPages = parseSelectedPages(pagesToSkip, 100);
      
      // Determine folder ID for customer rules
      const folderId = useCustomerRules ? extractionFolderId : undefined;
      
      const skipInfo = pagesToSkip.trim() ? ` (skipping pages: ${pagesToSkip})` : '';
      const folderInfo = folderId ? ` with customer rules` : '';
      setLoading(true, `Starting extraction${skipInfo}${folderInfo}...`);
      console.log(`📥 Using already-uploaded file: ${uploadedPdfFilename}`);
      if (selectedPages) {
        console.log(`📄 Processing only pages: ${selectedPages.join(', ')}`);
      }
      if (folderId) {
        console.log(`📁 Using customer folder rules: ${folderId}`);
      }
      
      // Call extract all API with streaming progress - pass filename instead of file
      const result = await bomApi.extractAllPages(
        null,  // No file upload needed
        false,
        (progress) => {
          setLoadingProgress({
            message: progress.message,
            progress: progress.progress,
            logs: [progress.message]
          });
          setLoading(true, progress.message);
        },
        () => {
          setLoadingProgress({
            message: 'Extraction complete!',
            progress: 100,
            logs: []
          });
        },
        (error) => {
          showToast('error', `Extraction failed: ${error}`);
          setLoading(false);
          setIsExtracting(false);
        },
        uploadedPdfFilename,  // Pass filename to use already-uploaded file
        folderId || null,     // Folder ID for customer-specific rules
        selectedPages         // Pages to process (null = all pages)
      );
      
      // Validate result structure
      if (!result || !result.main_bom || !result.auxiliary_bom) {
        throw new Error('Invalid extraction result: missing main_bom or auxiliary_bom');
      }
      
      setLoadingProgress(null);
      setLoading(true, 'Processing and consolidating extracted items...');
      
      // Store BOM data for export
      setExtractedBomData({
        main_bom: result.main_bom || [],
        auxiliary_bom: result.auxiliary_bom || []
      });
      
      // Clear existing data
      clearExtractedItems();
      setComponents([]);
      setManufacturers([]);
      setFeatures([]);
      setDimensions([]);
      
      // Convert inference results to BOM items format - use helper to pass all fields
      const mainBomItems: BOMItem[] = (result.main_bom || []).map((item: any) => convertBackendItemToBOMItem(item, false));
      const auxiliaryBomItems: BOMItem[] = (result.auxiliary_bom || []).map((item: any) => convertBackendItemToBOMItem(item, true));
      
      // Add all items to the BOM
      const allItems = [...mainBomItems, ...auxiliaryBomItems];
      addExtractedItems(allItems);
      
      // Derive manufacturers and components data for tabs
      deriveTabData(allItems);
      
      // Set totals
      setTotalPages(1);
      setCurrentPage(2); // Mark as complete
      
      // Automatically save extraction to database
      setLoading(true, 'Saving extraction results...');
      try {
        const saveResult = await bomApi.finalizeExtraction(
          uploadedPdfFilename!,
          uploadedXlsFilename,
          allItems,
          selectedModel
        );
        
        // Attach extraction_ids to items for verification
        const finalizeResult = saveResult as any;
        if (finalizeResult.extraction_ids && finalizeResult.extraction_ids.length === allItems.length) {
          allItems.forEach((item, idx) => {
            (item as any).extraction_id = finalizeResult.extraction_ids[idx];
          });
          // Update state with IDs
          addExtractedItems(allItems);
        }
        
        // Show success message with stats
        const stats = result.stats;
        showToast(
          'success',
          `Extracted and saved ${stats.main_items} main items + ${stats.auxiliary_items} auxiliary items from ALL pages. KB v${result.kb_version}`
        );
      } catch (saveError: any) {
        showToast('error', `Extraction completed but failed to save: ${saveError.message}`);
      }
      
      // Switch to BOM tab
      setCurrentTab('bom');
    } catch (error: any) {
      showToast('error', `Extract All failed: ${error.message}`);
      setLoadingProgress(null);
    } finally {
      setIsExtracting(false);
      setLoading(false);
      // Keep progress visible for a moment, then clear
      setTimeout(() => {
        setLoadingProgress(null);
      }, 2000);
    }
  }, [
    uploadedPdfUrl,
    uploadedPdfFilename,
    uploadedXlsFilename,
    selectedModel,
    clearExtractedItems,
    addExtractedItems,
    setComponents,
    setManufacturers,
    setFeatures,
    setDimensions,
    setTotalPages,
    setCurrentPage,
    setLoading,
    setLoadingProgress,
    showToast,
    deriveTabData,
    setCurrentTab,
    setExtractedBomData,
    setIsExtracting,
    pagesToSkip,
    useCustomerRules,
    extractionFolderId,
    parseSelectedPages,
  ]);

  // Export to Excel - use fresh extraction data if available, otherwise convert extractedItems
  const handleExportExcel = useCallback(async () => {
    if (!extractedItems || extractedItems.length === 0) {
      showToast('error', 'No BOM data to export. Please extract first.');
      return;
    }

    setLoading(true, 'Exporting to Excel...');
    
    try {
      let bomData;
      
      // PRIORITY: Use extractedBomData if available (fresh extraction results)
      // This ensures we export what was just extracted, not old database data
      if (extractedBomData && (extractedBomData.main_bom.length > 0 || extractedBomData.auxiliary_bom.length > 0)) {
        console.log('✅ Using fresh extraction data from extractedBomData');
        bomData = extractedBomData;
      } else {
        // Fallback: Convert extractedItems to export format
        console.log('⚠️  Using extractedItems (converted format)');
        const mainBom: any[] = [];
        const auxiliaryBom: any[] = [];
        
        extractedItems.forEach((item: BOMItem) => {
          const itemData: any = {
            reference: item.reference || '',
            description: item.description || item.raw_line || '',
            manufacturer_suggestion: item.manufacturer_suggestion || item.manufacturer || '',
            part_number: item.part_number || item.pn_guess || '',
            qty: item.qty || item.quantity || 1,
            component_type: item.component_type || item.type || '',
            confidence: item.confidence || 0,
            page: item.page || 1,
            pages: item.pages || (item.page ? [item.page] : []),
            notes: item.notes || '',
            erp_match: (item as any).erp_match || null,
            erp_matches: (item as any).erp_matches || [],
          };
          
          if ((item as any)._isAuxiliary) {
            auxiliaryBom.push(itemData);
          } else {
            mainBom.push(itemData);
          }
        });
        
        bomData = {
          main_bom: mainBom,
          auxiliary_bom: auxiliaryBom
        };
      }
      
      const blob = await bomApi.exportToExcel(bomData);
      
      // Extract original filename (remove UUID prefix if present)
      let pdfName = 'bom';
      if (uploadedPdfFilename) {
        pdfName = uploadedPdfFilename;
        if (pdfName.includes('_')) {
          const parts = pdfName.split('_', 2);
          if (parts[0].length === 32 && /^[0-9a-f]+$/i.test(parts[0])) {
            pdfName = parts[1] || pdfName;
          }
        }
        pdfName = pdfName.replace('.pdf', '');
      }
      const filename = `${pdfName}_extracted_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Helper function for fallback download
      const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      };
      
      // Try File System Access API first (Chrome/Edge only)
      if ('showSaveFilePicker' in window) {
        try {
          // Show "Save As" dialog with folder picker
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'Excel Files',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
            }]
          });
          
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          showToast('success', 'BOM exported to Excel successfully!');
        } catch (err: any) {
          // User cancelled or error - fallback to regular download
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to download:', err);
            downloadFile(blob, filename);
            showToast('success', 'BOM exported to Excel successfully!');
          }
        }
      } else {
        // Fallback for browsers that don't support File System Access API
        downloadFile(blob, filename);
        showToast('success', 'BOM exported to Excel successfully!');
      }
    } catch (error: any) {
      showToast('error', `Export failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [extractedItems, uploadedPdfFilename, setLoading, showToast, extractedBomData]);

  // Match ERP Parts - enrich existing BOM with part numbers
  const handleMatchErpParts = useCallback(async () => {
    if (!extractedItems || extractedItems.length === 0) {
      showToast('error', 'No BOM data to match. Please extract first.');
      return;
    }

    setLoading(true, 'Matching against ERP catalog...');
    
    try {
      let bomData;
      
      // Use extractedBomData if available (fresh extraction results)
      if (extractedBomData && (extractedBomData.main_bom.length > 0 || extractedBomData.auxiliary_bom.length > 0)) {
        console.log('✅ Using fresh extraction data for ERP matching');
        bomData = extractedBomData;
      } else {
        // Fallback: Convert extractedItems to export format
        console.log('⚠️  Using extractedItems (converted format) for ERP matching');
        const mainBom: any[] = [];
        const auxiliaryBom: any[] = [];
        
        extractedItems.forEach((item: BOMItem) => {
          const itemData: any = {
            reference: item.reference || '',
            description: item.description || item.raw_line || '',
            manufacturer_suggestion: item.manufacturer_suggestion || item.manufacturer || '',
            part_number: item.part_number || item.pn_guess || '',
            qty: item.qty || item.quantity || 1,
            component_type: item.component_type || item.type || '',
            confidence: item.confidence || 0,
            page: item.page || 1,
            pages: item.pages || (item.page ? [item.page] : []),
            notes: item.notes || '',
            erp_match: (item as any).erp_match || null,
            erp_matches: (item as any).erp_matches || [],
          };
          
          if ((item as any)._isAuxiliary) {
            auxiliaryBom.push(itemData);
          } else {
            mainBom.push(itemData);
          }
        });
        
        bomData = {
          main_bom: mainBom,
          auxiliary_bom: auxiliaryBom
        };
      }
      
      // Call ERP matching API
      const result = await bomApi.matchErpParts(bomData);
      
      // Update extractedBomData with enriched results
      if (extractedBomData) {
        setExtractedBomData({
          main_bom: result.main_bom,
          auxiliary_bom: result.auxiliary_bom
        });
      }
      
      // Update extractedItems with enriched data
      const allItems = [...result.main_bom, ...result.auxiliary_bom];
      const updatedItems: BOMItem[] = allItems.map((item: any) => convertBackendItemToBOMItem(item, (item as any)._isAuxiliary || false));
      addExtractedItems(updatedItems);
      
      showToast('success', `ERP matching complete! Matched ${result.matched_count} items.`);
    } catch (error: any) {
      showToast('error', `ERP matching failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [extractedItems, extractedBomData, setLoading, showToast, addExtractedItems, setExtractedBomData, convertBackendItemToBOMItem]);

  // NEW: Compare Page Extraction (Text vs LLM)
  const handleComparePage = useCallback(async () => {
    if (!uploadedPdfUrl) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsComparing(true);
    setLoading(true, `Comparing page ${comparePageNum}...`);
    
    try {
      // Get the file from the uploaded URL
      const response = await fetch(uploadedPdfUrl);
      const blob = await response.blob();
      const file = new File([blob], uploadedPdfFilename || 'document.pdf', { type: 'application/pdf' });
      
      // Call compare API
      const result = await bomApi.comparePage(file, Number(comparePageNum), compareLLMModel);
      
      // Store comparison result
      setComparisonResult(result);
      
      showToast('success', `Comparison complete for page ${comparePageNum}`);
    } catch (error: any) {
      showToast('error', `Comparison failed: ${error.message}`);
    } finally {
      setIsComparing(false);
      setLoading(false);
    }
  }, [
    uploadedPdfUrl,
    uploadedPdfFilename,
    comparePageNum,
    compareLLMModel,
    setLoading,
    showToast,
  ]);

  // NEW: Switch to LLM extraction
  const handleSwitchToLLM = useCallback(async () => {
    if (!uploadedPdfUrl) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsExtracting(true);
    setLoading(true, 'Extracting ALL pages with LLM...');
    
    try {
      // Use already-uploaded file instead of re-downloading
      if (!uploadedPdfFilename) {
        throw new Error('PDF filename is not available');
      }
      
      console.log(`📥 Using already-uploaded file for LLM extraction: ${uploadedPdfFilename}`);
      
      // Call extract all API with LLM enabled and streaming progress
      const result = await bomApi.extractAllPages(
        null,  // No file upload needed - use already-uploaded file
        true,
        (progress) => {
          setLoadingProgress({
            message: progress.message,
            progress: progress.progress,
            logs: [progress.message]
          });
          setLoading(true, progress.message);
        },
        () => {
          setLoadingProgress({
            message: 'Extraction complete!',
            progress: 100,
            logs: []
          });
        },
        (error) => {
          showToast('error', `Extraction failed: ${error}`);
          setLoading(false);
          setIsExtracting(false);
        },
        uploadedPdfFilename  // Pass filename to use already-uploaded file
      ); // LLM enabled
      
      // Validate result structure
      if (!result || !result.main_bom || !result.auxiliary_bom) {
        throw new Error('Invalid extraction result: missing main_bom or auxiliary_bom');
      }
      
      setLoadingProgress(null);
      
      // Store BOM data
      setExtractedBomData({
        main_bom: result.main_bom || [],
        auxiliary_bom: result.auxiliary_bom || []
      });
      
      // Clear and repopulate
      clearExtractedItems();
      
      const mainBomItems: BOMItem[] = result.main_bom.map((item: any) => ({
        reference: item.reference,
        description: item.description,
        manufacturer: item.manufacturer_suggestion,
        part_number: item.part_number,
        quantity: item.qty,
        confidence: item.confidence,
        component_type: item.component_type,
        notes: item.notes,
        page: item.page || 1,  // Use actual page from backend
      }));
      
      const auxiliaryBomItems: BOMItem[] = result.auxiliary_bom.map((item: any) => ({
        reference: item.reference,
        description: item.description,
        manufacturer: item.manufacturer_suggestion,
        part_number: item.part_number || '',
        quantity: item.qty,
        confidence: item.confidence,
        component_type: item.component_type,
        notes: item.notes || item.calculation_basis,
        page: item.page || 1,  // Use actual page from backend
        _isAuxiliary: true,
      }));
      
      addExtractedItems([...mainBomItems, ...auxiliaryBomItems]);
      
      showToast('success', `Switched to LLM extraction: ${result.stats.main_items} main + ${result.stats.auxiliary_items} auxiliary items`);
      setShowCompareMode(false); // Exit compare mode
      setCurrentTab('bom');
    } catch (error: any) {
      showToast('error', `LLM extraction failed: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoading(false);
    }
  }, [
    uploadedPdfUrl,
    uploadedPdfFilename,
    clearExtractedItems,
    addExtractedItems,
    setLoading,
    showToast,
  ]);

  // Handle page extraction (LEGACY - page by page)
  const handleExtractPage = useCallback(async () => {
    if (!uploadedPdfFilename) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsExtracting(true);
    setLoading(true, `Extracting Page ${currentPage}...`);

    try {
      const result = await bomApi.extractPage(
        uploadedPdfFilename,
        currentPage,
        selectedModel,
        forceReextract
      );

      if (result._from_cache) {
        showToast('info', `Loaded from cache (${new Date(result._cached_at || '').toLocaleString()})`);
      }

      if (result.total_pages && !totalPages) {
        setTotalPages(result.total_pages);
      }

      // Process Ask API results
      if (result.asks) {
        // BOM items
        if (result.asks.bom) {
          const bomItems: BOMItem[] = result.asks.bom.map((ask: any) => ({
            ...ask.data,
            page: currentPage,
            confidence: ask.confidence,
          }));
          addExtractedItems(bomItems);
        }

        // Components - use functional setState to avoid stale closure
        if (result.asks.components) {
          const newComponents = result.asks.components.map((ask: any) => ask.data);
          setComponents((prev) => [...prev, ...newComponents]);
        }

        // Manufacturers - use functional setState to avoid stale closure
        if (result.asks.manufacturers) {
          const newManufacturers = result.asks.manufacturers.map((ask: any) => ask.data);
          setManufacturers((prev) => [...prev, ...newManufacturers]);
        }

        // Metadata
        if (result.asks.metadata && result.asks.metadata.length > 0) {
          setMetadata(result.asks.metadata[0].data);
        }

        // Dimensions - use functional setState to avoid stale closure
        if (result.asks.dimensions) {
          const newDimensions = result.asks.dimensions.map((ask: any) => ask.data);
          setDimensions((prev) => [...prev, ...newDimensions]);
        }

        // Features - use functional setState to avoid stale closure
        if (result.asks.features) {
          const newFeatures = result.asks.features.map((ask: any) => ask.data);
          setFeatures((prev) => [...prev, ...newFeatures]);
        }
      }

      // Move to next page
      setCurrentPage(currentPage + 1);

      // Check if we've reached the end
      if (result.total_pages && currentPage >= result.total_pages) {
        // Automatically save when all pages are extracted
        setLoading(true, 'Saving extraction results...');
        try {
          const finalizeResult = await bomApi.finalizeExtraction(
            uploadedPdfFilename!,
            uploadedXlsFilename,
            extractedItems,
            selectedModel
          );
          
          // Attach extraction_ids to items for verification
          const result = finalizeResult as any;
          if (result.extraction_ids && result.extraction_ids.length === extractedItems.length) {
            extractedItems.forEach((item, idx) => {
              (item as any).extraction_id = result.extraction_ids[idx];
            });
            // Update state with IDs
            addExtractedItems(extractedItems);
          }
          
          showToast('success', 'All pages extracted and saved!');
        } catch (saveError: any) {
          showToast('error', `Extraction completed but failed to save: ${saveError.message}`);
        } finally {
          setLoading(false);
        }
      } else {
        showToast('success', `Page ${currentPage} extracted successfully`);
      }
    } catch (error: any) {
      showToast('error', `Extraction failed: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoading(false);
    }
  }, [
    uploadedPdfFilename,
    currentPage,
    selectedModel,
    forceReextract,
    totalPages,
    components,
    manufacturers,
    dimensions,
    features,
    setCurrentPage,
    setTotalPages,
    addExtractedItems,
    setComponents,
    setManufacturers,
    setMetadata,
    setDimensions,
    setFeatures,
    setLoading,
    showToast,
  ]);

  // Handle finalize
  const handleFinalize = useCallback(async () => {
    setLoading(true, 'Saving extraction results...');

    try {
      const result = await bomApi.finalizeExtraction(
        uploadedPdfFilename!,
        uploadedXlsFilename,
        extractedItems,
        selectedModel
      );

      // Attach extraction_ids to items for verification
      const finalizeResult = result as any;
      if (finalizeResult.extraction_ids && finalizeResult.extraction_ids.length === extractedItems.length) {
        extractedItems.forEach((item, idx) => {
          (item as any).extraction_id = finalizeResult.extraction_ids[idx];
        });
        // Update state with IDs
        addExtractedItems(extractedItems);
      }

      showToast('success', 'Extraction saved successfully!');

      // Keep extracted items visible - don't clear the table
      // Only reset the finalize button state
      setShowFinalize(false);
    } catch (error: any) {
      showToast('error', `Failed to save: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    uploadedPdfFilename,
    uploadedXlsFilename,
    extractedItems,
    selectedModel,
    setCurrentPage,
    setTotalPages,
    clearExtractedItems,
    setLoading,
    showToast,
  ]);

  // Clear cache handlers
  const handleClearPageCache = useCallback(async () => {
    if (!uploadedPdfFilename) return;

    if (!window.confirm(`Clear cached data for page ${currentPage}? This will require re-extraction.`)) {
      return;
    }

    try {
      // Clear database cache for this page
      await bomApi.clearPageCache(uploadedPdfFilename, currentPage, selectedModel);
      
      // Note: We don't clear frontend state here because user might have other pages extracted
      // They can use "Clear All" to reset everything
      
      showToast('success', `Page ${currentPage} cache cleared - re-extract to get fresh data`);
    } catch (error: any) {
      showToast('error', 'Failed to clear page cache');
    }
  }, [uploadedPdfFilename, currentPage, selectedModel, showToast]);

  const handleClearPdfCache = useCallback(async () => {
    if (!uploadedPdfFilename) return;

    if (!window.confirm(`Clear screen and unload PDF/Excel? This will reset the UI but keep extractions in the database.`)) {
      return;
    }

    try {
      // Clear frontend state - unload PDF and Excel
      clearExtractedItems();
      setComponents([]);
      setManufacturers([]);
      setMetadata(null);
      setFeatures([]);
      setDimensions([]);
      setCurrentPage(1);
      setTotalPages(null);
      setShowFinalize(false);
      
      // Clear uploaded files
      clearUploadedPdf();
      clearUploadedXls();
      
      showToast('success', 'Screen cleared. You can now upload new PDFs.');
    } catch (error: any) {
      showToast('error', `Failed to clear screen: ${error.message}`);
    }
  }, [
    uploadedPdfFilename, 
    showToast, 
    clearExtractedItems,
    setComponents,
    setManufacturers,
    setMetadata,
    setFeatures,
    setDimensions,
    setCurrentPage,
    setTotalPages,
    clearUploadedPdf,
    clearUploadedXls
  ]);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Upload BOM Documents
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload a PDF schematic and use: <strong>Extract All</strong> (text-based, default) | <strong>Export to Excel</strong> (after extraction)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<UploadIcon />}
            onClick={() => {
              // Reset everything for new document
              clearUploadedPdf();
              clearUploadedXls();
              clearExtractedItems();
              setComponents([]);
              setManufacturers([]);
              setMetadata(null);
              setFeatures([]);
              setDimensions([]);
              setTotalPages(0);
              setCurrentPage(1);
              showToast('info', 'Ready for new document');
            }}
          >
            UPLOAD NEW DOCUMENT
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleClearPdfCache}
          >
            CLEAR EXISTING
          </Button>
        </Box>
      </Box>

      {/* Extraction Controls */}
      {uploadedPdfFilename && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Left side: Filename and page count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {extractOriginalFilename(uploadedPdfFilename)}
              </Typography>
              {totalPages && (
                <Chip 
                  label={`${totalPages} page${totalPages !== 1 ? 's' : ''}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            {/* Right side: Action buttons - always in same row */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
              <Button
                variant="contained"
                startIcon={<ExtractIcon />}
                onClick={handleExtractClick}
                disabled={isExtracting}
                size="medium"
                sx={{ 
                  backgroundColor: 'warning.main',
                  '&:hover': { backgroundColor: 'warning.dark' },
                  color: 'white',
                  whiteSpace: 'nowrap'
                }}
              >
                EXTRACT ALL
              </Button>
              <Button
                variant="outlined"
                onClick={handleMatchErpParts}
                disabled={!extractedItems || extractedItems.length === 0}
                color="primary"
                size="medium"
                sx={{ whiteSpace: 'nowrap' }}
              >
                MATCH ERP PARTS
              </Button>
              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                onClick={handleExportExcel}
                disabled={!extractedItems || extractedItems.length === 0}
                size="medium"
                sx={{ 
                  backgroundColor: 'warning.main',
                  '&:hover': { backgroundColor: 'warning.dark' },
                  color: 'white',
                  whiteSpace: 'nowrap'
                }}
              >
                EXPORT TO EXCEL
              </Button>
            </Box>
          </Box>

          {/* Progress */}
          {totalPages && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Page {Math.min(currentPage, totalPages)} of {totalPages}
                </Typography>
                <Typography variant="body2">
                  {extractedItems.length} items extracted
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(Math.min(currentPage - 1, totalPages) / totalPages) * 100}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Compare Mode Section */}
      {showCompareMode && uploadedPdfFilename && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🔬 Compare Mode: Text vs LLM Extraction
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test extraction methods page-by-page to see which performs better for unseen PDFs
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Page Number</InputLabel>
                <Select
                  value={comparePageNum}
                  onChange={(e: SelectChangeEvent) => setComparePageNum(e.target.value)}
                  label="Page Number"
                >
                  {[...Array(50)].map((_, i) => (
                    <MenuItem key={i + 1} value={String(i + 1)}>
                      Page {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>LLM Model</InputLabel>
                <Select
                  value={compareLLMModel}
                  onChange={(e: SelectChangeEvent) => setCompareLLMModel(e.target.value)}
                  label="LLM Model"
                >
                  <MenuItem value="claude">Claude 3.5 Sonnet</MenuItem>
                  <MenuItem value="gpt4o">GPT-4o</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleComparePage}
                  disabled={isComparing}
                  fullWidth
                >
                  Run Comparison
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCompareMode(false);
                    setComparisonResult(null);
                  }}
                >
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Comparison Results */}
          {comparisonResult && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      📝 Text Extraction ({comparisonResult.text_extraction.count} items)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {comparisonResult.text_extraction.method}
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 1 }}>
                      {comparisonResult.text_extraction.items.map((item: any, idx: number) => (
                        <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.reference || 'N/A'}: {item.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mfr: {item.manufacturer_suggestion || 'Unknown'} | Confidence: {(item.confidence * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      🤖 LLM Extraction ({comparisonResult.llm_extraction.count} items)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {comparisonResult.llm_extraction.method}
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 1 }}>
                      {comparisonResult.llm_extraction.items.map((item: any, idx: number) => (
                        <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.reference || 'N/A'}: {item.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mfr: {item.manufacturer || 'Unknown'} | Confidence: {((item.confidence || 0) * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Switch to LLM button */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleSwitchToLLM}
                  disabled={isExtracting}
                  size="large"
                >
                  🔄 Switch to LLM Extraction (Full PDF)
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Use this if LLM extraction looks better. Will extract ALL pages with LLM vision.
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Previously Uploaded PDFs Section - Above PDF Viewer */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <IconButton
            onClick={() => setFileDrawerOpen(!fileDrawerOpen)}
            size="small"
            sx={{ mr: 1 }}
          >
            {fileDrawerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', flexGrow: 1 }} onClick={() => setFileDrawerOpen(!fileDrawerOpen)}>
            {fileDrawerOpen ? 'Hide' : 'Show'} Previously Uploaded PDFs ({previousPdfs.length})
          </Typography>
        </Box>

        {/* Collapsible File Drawer */}
        <Collapse in={fileDrawerOpen}>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {loadingPdfs ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Loading PDFs...</Typography>
              </Box>
            ) : previousPdfs.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No PDFs with extractions found</Typography>
              </Box>
            ) : (
              <List dense>
                {previousPdfs.map((pdf, index) => (
                  <React.Fragment key={pdf.filename}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleSelectPreviousPdf(pdf)}>
                        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <ListItemText
                          primary={pdf.original_name}
                          secondary={new Date(pdf.uploaded_at).toLocaleDateString()}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < previousPdfs.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* PDF Viewer Section - Above BOM Table */}
      {uploadedPdfUrl && (
        <Paper sx={{ mb: 3 }}>
          <PDFViewer 
            pdfUrl={uploadedPdfUrl}
            isExpanded={expandedViewer === 'pdf'}
            onToggleExpand={() => setExpandedViewer(expandedViewer === 'pdf' ? null : 'pdf')}
            currentPage={currentPage}
            selectedItem={selectedItem}
            onClose={clearUploadedPdf}
          />
        </Paper>
      )}

      {/* Results Tabs */}
      {extractedItems.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
            <Tab label="BOM" value="bom" />
            <Tab label={`Components (${components.length})`} value="components" />
            <Tab label={`Manufacturers (${manufacturers.length})`} value="manufacturers" />
            <Tab label="Metadata" value="metadata" />
            <Tab label={`Features (${features.length})`} value="features" />
            <Tab label={`Dimensions (${dimensions.length})`} value="dimensions" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {currentTab === 'bom' && <BOMTable items={extractedItems} onItemSelect={setSelectedItem} />}
            {currentTab === 'components' && <ComponentsTab components={components} />}
            {currentTab === 'manufacturers' && <ManufacturersTab manufacturers={manufacturers} />}
            {currentTab === 'metadata' && <MetadataTab metadata={metadata} />}
            {currentTab === 'features' && <FeaturesTab features={features} />}
            {currentTab === 'dimensions' && <DimensionsTab dimensions={dimensions} />}
          </Box>
        </Paper>
      )}

      {/* Upload Section */}
      <Grid container spacing={3}>
        {/* PDF Upload - Only show when no PDF is loaded */}
        {!uploadedPdfUrl && (
          <Grid item xs={12}>
            {/* Customer Folder Selection */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel id="upload-folder-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FolderIcon fontSize="small" />
                    Upload to Customer Folder (Optional)
                  </Box>
                </InputLabel>
                <Select
                  labelId="upload-folder-label"
                  value={selectedUploadFolder}
                  onChange={(e) => setSelectedUploadFolder(e.target.value)}
                  label="Upload to Customer Folder (Optional)"
                >
                  <MenuItem value="">
                    <em>No folder - Global</em>
                  </MenuItem>
                  {customerFolders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon fontSize="small" color="primary" />
                        {folder.customer_name}
                        <Chip 
                          label={`${folder.file_count} files`} 
                          size="small" 
                          sx={{ ml: 1, height: 20 }} 
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <FileUploadZone
              title="PDF BOM Document *"
              accept={{ 'application/pdf': ['.pdf'] }}
              onFileSelect={handlePdfUpload}
              uploadedFile={null}
              onClear={clearUploadedPdf}
            />
          </Grid>
        )}

        {/* Excel Viewer - moved to next row, full width */}
        <Grid item xs={12}>
            <FileUploadZone
              title="Excel Ground Truth (Optional)"
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls']
              }}
              onFileSelect={handleExcelUpload}
            uploadedFile={
              uploadedXlsFilename
                ? { filename: uploadedXlsFilename, url: uploadedXlsUrl || '' }
                : null
            }
            onClear={clearUploadedXls}
          />
          {uploadedXlsUrl && (
            <ExcelViewer 
              xlsUrl={uploadedXlsUrl}
              isExpanded={expandedViewer === 'excel'}
              onToggleExpand={() => setExpandedViewer(expandedViewer === 'excel' ? null : 'excel')}
            />
          )}
        </Grid>
      </Grid>

      {/* Extraction Options Dialog */}
      <Dialog 
        open={extractOptionsOpen} 
        onClose={() => setExtractOptionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          Extraction Options
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Pages to Skip */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📄 Pages to Skip
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., 1, 3, 5-7 (leave empty to process all pages)"
                value={pagesToSkip}
                onChange={(e) => setPagesToSkip(e.target.value)}
                helperText="Enter page numbers to skip, separated by commas. Use ranges like 5-7."
              />
            </Box>

            {/* Customer Folder / Rules Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RuleIcon fontSize="small" />
                Customer-Specific Rules
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomerRules}
                    onChange={(e) => setUseCustomerRules(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Enable customer-specific rules during extraction
                  </Typography>
                }
              />

              {useCustomerRules && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="extract-folder-label">Customer Folder</InputLabel>
                  <Select
                    labelId="extract-folder-label"
                    value={extractionFolderId}
                    onChange={(e) => setExtractionFolderId(e.target.value)}
                    label="Customer Folder"
                  >
                    <MenuItem value="">
                      <em>Global rules only</em>
                    </MenuItem>
                    {customerFolders.map((folder) => (
                      <MenuItem key={folder.id} value={folder.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FolderIcon fontSize="small" color="primary" />
                          {folder.customer_name}
                          {folder.rule_count > 0 && (
                            <Chip 
                              label={`${folder.rule_count} rules`} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1, height: 20 }} 
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Select a customer folder to apply its specific extraction rules
                  </Typography>
                </FormControl>
              )}
            </Box>

            {/* Summary */}
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Extraction will:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  <li>Process {pagesToSkip.trim() ? `all pages except: ${pagesToSkip}` : 'all pages'}</li>
                  <li>
                    {useCustomerRules 
                      ? extractionFolderId 
                        ? `Apply global + customer-specific rules for ${customerFolders.find(f => f.id === extractionFolderId)?.customer_name || 'selected customer'}`
                        : 'Apply global rules only'
                      : 'Apply global rules only (customer rules disabled)'
                    }
                  </li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExtractOptionsOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleExtractAll}
            startIcon={<ExtractIcon />}
          >
            Start Extraction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadView;

